/**
 * Búsqueda de órdenes (`GET /api/v1/orders?search=...`). Reemplaza el placeholder
 * de `ROUTES.ordersSearch`. Equivale a `search_orders.html` del SSR.
 *
 * El backend filtra `search` (número o cliente) **dentro de un estado** (el
 * listado siempre lleva `status`). Esta pantalla deja elegir el estado a buscar
 * (por defecto, pendientes, como la vista de búsqueda del SSR) y reutiliza la
 * tabla común, la paginación y el modal de detalle. Orquestación de hooks; sin
 * `fetch` (la sede va por `X-Sede-Id` en lib/api).
 */
import { useState } from "react";

import { Button, Card, CardContent, Input } from "@/components/ui";

import { STATUS_LABEL } from "../constants";
import { useOrders } from "../hooks/useOrders";
import type { OrderStatus } from "../types";
import { OrderDetailDialog } from "./OrderDetailDialog";
import { OrderPagination } from "./OrderPagination";
import { OrderTable } from "./OrderTable";

const PER_PAGE = 20;

const STATUS_OPTIONS: OrderStatus[] = [
  "pendiente",
  "completado",
  "entregado",
  "anulada",
];

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "No se pudieron cargar las órdenes.";
}

export function OrdersSearchScreen() {
  const [status, setStatus] = useState<OrderStatus>("pendiente");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<number | null>(null);

  const orders = useOrders({ status, search, page, per_page: PER_PAGE });

  function applySearch(event: React.FormEvent) {
    event.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold text-slate-900">Buscar órdenes</h1>

      <form className="flex flex-wrap items-end gap-2" onSubmit={applySearch}>
        <div className="flex flex-col gap-1">
          <label htmlFor="orders-search-term" className="text-sm text-slate-600">
            Número o cliente
          </label>
          <Input
            id="orders-search-term"
            className="w-64"
            placeholder="Ej. 5000 o Acme"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="orders-search-status" className="text-sm text-slate-600">
            Estado
          </label>
          <select
            id="orders-search-status"
            className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as OrderStatus);
              setPage(1);
            }}
          >
            {STATUS_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABEL[value]}
              </option>
            ))}
          </select>
        </div>
        <Button type="submit" variant="primary" size="md">
          Buscar
        </Button>
      </form>

      <Card>
        <CardContent className="p-0">
          {orders.isPending ? (
            <p className="px-1 py-8 text-center text-sm text-slate-500" role="status">
              Cargando órdenes…
            </p>
          ) : orders.isError ? (
            <p className="px-1 py-8 text-center text-sm text-red-600" role="alert">
              {errorMessage(orders.error)}
            </p>
          ) : (
            <>
              <OrderTable items={orders.data.items} onViewDetail={setDetailId} />
              <div className="border-t border-slate-200">
                <OrderPagination
                  page={orders.data.page}
                  pages={orders.data.pages}
                  total={orders.data.total}
                  onPageChange={setPage}
                  disabled={orders.isFetching}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <OrderDetailDialog orderId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
